// Вкладка «Журнал»: предупреждения, ошибки и обмены с ИИ. Список лёгкий (без
// тел), тело записи — промпт и ответ модели — тянется при раскрытии строки.
// Хранение и ротация описаны в server/logbook.mjs.
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useI18n, useT } from '../i18n';
import { errorKey } from '../auth';
import type { ApiError } from '../auth';
import { clearLogs, fetchLog, listLogs } from './api';
import type { AdminLogDetail, AdminLogRow, LogLevel, LogsPage } from './api';
import styles from './LogsPanel.module.css';

const PAGE_SIZE = 50;

/** Фильтры-вкладки: проблемы (warn+error), обмены с ИИ (info), всё подряд. */
const FILTERS = {
  problems: ['warn', 'error'] as LogLevel[],
  ai: ['info'] as LogLevel[],
  all: [] as LogLevel[],
};
type Filter = keyof typeof FILTERS;

/** Раскрытая запись: parsed — разобранный details, raw — как есть, если не JSON. */
interface Opened {
  log: AdminLogDetail;
  parsed: Record<string, unknown> | null;
}

function parseDetails(details: string | null): Record<string, unknown> | null {
  if (!details) return null;
  try {
    const value: unknown = JSON.parse(details);
    return typeof value === 'object' && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Трасса обмена с моделью внутри details — если она там есть. */
function traceOf(parsed: Record<string, unknown> | null): {
  messages: Array<{ role: string; content: string }>;
  responses: string[];
} | null {
  const trace = parsed?.trace;
  if (typeof trace !== 'object' || trace === null) return null;
  const t = trace as { messages?: unknown; responses?: unknown };
  return {
    messages: Array.isArray(t.messages)
      ? (t.messages as Array<{ role: string; content: string }>)
      : [],
    responses: Array.isArray(t.responses) ? (t.responses as string[]) : [],
  };
}

export function LogsPanel(): ReactElement {
  const t = useT();
  const { lang } = useI18n();

  const [filter, setFilter] = useState<Filter>('problems');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<LogsPage | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [openedId, setOpenedId] = useState<number | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);

  const reload = useCallback(async () => {
    const res = await listLogs({ levels: FILTERS[filter], limit: PAGE_SIZE, offset });
    if (res.ok) {
      setPage(res.value);
      setError(null);
    } else {
      setError(res.error);
    }
  }, [filter, offset]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function toggle(row: AdminLogRow) {
    if (openedId === row.id) {
      setOpenedId(null);
      setOpened(null);
      return;
    }
    setOpenedId(row.id);
    setOpened(null);
    const res = await fetchLog(row.id);
    if (res.ok) setOpened({ log: res.value.log, parsed: parseDetails(res.value.log.details) });
    else setError(res.error);
  }

  async function handleClear() {
    if (!window.confirm(t('admin.logs.clear.confirm'))) return;
    const res = await clearLogs();
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setOpenedId(null);
    setOpened(null);
    setOffset(0);
    await reload();
  }

  const format = (ms: number) =>
    new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'medium' }).format(ms);

  const total = page?.total ?? 0;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className={styles.panel}>
      <div className={styles.toolbar}>
        <div className={styles.filters} role="group">
          {(Object.keys(FILTERS) as Filter[]).map((key) => (
            <button
              key={key}
              type="button"
              className={filter === key ? `btn btn-sm ${styles.filterActive}` : 'btn btn-sm'}
              aria-pressed={filter === key}
              onClick={() => {
                setFilter(key);
                setOffset(0);
                setOpenedId(null);
                setOpened(null);
              }}
            >
              {t(`admin.logs.filter.${key}`)}
            </button>
          ))}
        </div>
        <span className={styles.spacer} />
        <span className={styles.retention}>{t('admin.logs.retention')}</span>
        <button type="button" className="btn btn-sm btn-danger" onClick={() => void handleClear()}>
          {t('admin.logs.clear')}
        </button>
      </div>

      {error && (
        <p className={styles.error} role="alert">
          {t(errorKey(error))}
        </p>
      )}

      {page && page.logs.length === 0 ? (
        <p className={styles.empty}>{t('admin.logs.empty')}</p>
      ) : (
        <ul className={styles.list}>
          {(page?.logs ?? []).map((row) => (
            <li key={row.id} className={styles.item}>
              <button type="button" className={styles.row} onClick={() => void toggle(row)}>
                <span className={`mono ${styles.time}`}>{format(row.ts)}</span>
                <span
                  className={`${styles.level} ${
                    row.level === 'error' ? styles.levelError : row.level === 'warn' ? styles.levelWarn : styles.levelInfo
                  }`}
                >
                  {row.level}
                </span>
                {row.scope && <span className={styles.scope}>{row.scope}</span>}
                <span className={styles.message}>{row.message}</span>
              </button>

              {openedId === row.id && (
                <div className={styles.details}>
                  {!opened ? (
                    <p className={styles.loading}>…</p>
                  ) : opened.log.details === null ? (
                    <p className={styles.loading}>{t('admin.logs.noDetails')}</p>
                  ) : opened.parsed ? (
                    <DetailsView parsed={opened.parsed} raw={opened.log.details} />
                  ) : (
                    <pre className={styles.pre}>{opened.log.details}</pre>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {total > PAGE_SIZE && (
        <div className={styles.pager}>
          <button
            type="button"
            className="btn btn-sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            {t('admin.prev')}
          </button>
          <span className={styles.pageInfo}>
            {t('admin.page', { from: total === 0 ? 0 : offset + 1, to, total })}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={to >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            {t('admin.next')}
          </button>
        </div>
      )}
    </div>
  );
}

/** Тело записи: сначала сводка (всё, кроме трассы), затем запрос и ответы. */
function DetailsView({ parsed, raw }: { parsed: Record<string, unknown>; raw: string }): ReactElement {
  const t = useT();
  const trace = traceOf(parsed);
  const meta = Object.fromEntries(Object.entries(parsed).filter(([key]) => key !== 'trace'));

  return (
    <div className={styles.sections}>
      {Object.keys(meta).length > 0 && (
        <section>
          <h4 className={styles.sectionTitle}>{t('admin.logs.details.meta')}</h4>
          <pre className={styles.pre}>{JSON.stringify(meta, null, 2)}</pre>
        </section>
      )}
      {trace ? (
        <>
          {trace.messages.map((m, i) => (
            <section key={`m${i}`}>
              <h4 className={styles.sectionTitle}>
                {t('admin.logs.details.request')} · {m.role}
              </h4>
              <pre className={styles.pre}>{m.content}</pre>
            </section>
          ))}
          {trace.responses.map((r, i) => (
            <section key={`r${i}`}>
              <h4 className={styles.sectionTitle}>
                {t('admin.logs.details.response')}
                {trace.responses.length > 1 ? ` #${i + 1}` : ''}
              </h4>
              <pre className={styles.pre}>{r}</pre>
            </section>
          ))}
        </>
      ) : (
        Object.keys(meta).length === 0 && <pre className={styles.pre}>{raw}</pre>
      )}
    </div>
  );
}
